import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { COUNTRIES } from '@/types/user';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalCountrySelectorProps {
  value?: string;
  onValueChange: (countryCode: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const ModalCountrySelector: React.FC<ModalCountrySelectorProps> = ({
  value,
  onValueChange,
  placeholder = "Select country...",
  className,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedCountry = COUNTRIES.find(country => country.code === value);
  
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate dropdown position when opening
  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const dropdownHeight = 244; // max-h-60 = 240px + padding
      
      let top = rect.bottom + window.scrollY + 4;
      let left = rect.left + window.scrollX;
      let width = rect.width;
      
      // If dropdown would go off bottom of screen, position it above
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4;
      }
      
      // Ensure dropdown doesn't go off right edge of screen
      if (left + width > viewportWidth - 16) {
        left = viewportWidth - width - 16;
      }
      
      // Ensure dropdown doesn't go off left edge of screen
      if (left < 16) {
        left = 16;
        width = Math.min(width, viewportWidth - 32);
      }
      
      setDropdownPosition({ top, left, width });
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  const handleSelect = (countryCode: string) => {
    onValueChange(countryCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        updateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  // Create dropdown content to be portaled
  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl overflow-hidden touch-manipulation"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999, // Very high z-index to be above everything
        maxHeight: '240px',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Search input */}
      <div className="p-2 border-b border-gray-200 dark:border-gray-600">
        <input
          type="text"
          placeholder="Search countries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-gray-900 dark:text-gray-100"
          autoFocus
        />
      </div>
      
      {/* Options */}
      <div 
        className="overflow-y-auto" 
        style={{ 
          maxHeight: '192px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {filteredCountries.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            No countries found
          </div>
        ) : (
          filteredCountries.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleSelect(country.code)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none transition-colors text-left text-gray-900 dark:text-gray-100 touch-manipulation active:bg-gray-200 dark:active:bg-gray-600"
              style={{ 
                WebkitTapHighlightColor: 'transparent',
                WebkitUserSelect: 'none',
                userSelect: 'none'
              }}
            >
              <CountryFlag countryCode={country.code} size="sm" />
              <span>{country.name}</span>
            </button>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={cn(
          "flex h-12 w-full items-center justify-between rounded-xl border-0 px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation",
          className
        )}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          userSelect: 'none'
        }}
      >
        <span className="flex items-center gap-2 flex-1 text-left">
          {selectedCountry ? (
            <>
              <CountryFlag countryCode={selectedCountry.code} size="sm" />
              <span>{selectedCountry.name}</span>
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
      </button>

      {/* Portal the dropdown to document.body */}
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default ModalCountrySelector;
